import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const testimonials = [{
  name: 'MITZY',
  age: 55,
  quote: '"The Cooling Code helped me get ready for my Mount Everest Base Camp trek goal, a dream come true. I lost 30lbs. I feel 15 years younger. I have more energy and more confidence. I am much happier."',
  image: 'https://cdn.magicpatterns.com/uploads/k5vwLweiLvPW9qGaYWpvMx/Mujer_terno_azul.png'
}, {
  name: 'MANDI',
  age: 48,
  quote: '"The nighttime protocol was a game changer for me, I love it. So easy and incredibly effective, I love seeing myself in the mirror now. My stress belly is finally gone!"',
  image: 'https://cdn.magicpatterns.com/uploads/ne7HSdzSQT5uaGod8vqgDT/Mujer_terno_beige.png'
}, {
  name: 'LISA',
  age: 58,
  quote: '"I have been using the Cooling Code for a year now and I have lost 97lbs. I am not stating this to brag but to let everyone know it is possible to make these life changes at our age."',
  image: 'https://cdn.magicpatterns.com/uploads/55Z7B8Qs4XtWsp8TbyA8HZ/afro_terno_obscuro.png'
}, {
  name: 'CARMEN',
  age: 52,
  quote: '"I never thought I could feel this good again at 52. The Cooling Code eliminated my night sweats and I lost 25 lbs in just 8 weeks. My husband says I look like when we first met. I finally have my confidence back!"',
  image: 'https://cdn.magicpatterns.com/uploads/ptbDQiv4mzcUS7e8kavqzR/Mujer_vestido_vino.png'
}];
export function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };
  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(prevIndex => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) nextIndex = testimonials.length - 1;
      if (nextIndex >= testimonials.length) nextIndex = 0;
      return nextIndex;
    });
  }, []);
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);
    return () => clearInterval(timer);
  }, [isHovered, paginate]);
  return <section className="bg-white py-24 overflow-hidden" data-id="element-226">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227">
        <div className="text-center mb-16" data-id="element-228">
          <h2 className="text-sm font-bold tracking-widest text-gold uppercase mb-3" data-id="element-229">
            Real Results
          </h2>
          <h3 className="text-3xl md:text-5xl font-bold text-deep-teal mb-6" data-id="element-230">
            Women Like You Are Taking Back Control
          </h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-231">
            See how the Cooling Code is transforming lives, one night at a time.
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-232">
          {/* Carousel Container */}
          <div className="relative w-full" data-id="element-233">
            <AnimatePresence initial={false} custom={direction} data-id="element-234">
              <motion.div key={currentIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={1} onDragEnd={(e, {
              offset,
              velocity
            }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }} className="w-full px-4 sm:px-12" data-id="element-235">
                <div className="bg-white rounded-3xl shadow-premium overflow-hidden border border-gray-100 flex flex-col mx-auto max-w-2xl" data-id="element-236">
                  {/* Image Area */}
                  <div className="relative w-full overflow-hidden shrink-0 bg-gray-50" data-id="element-237">
                    <img src={testimonials[currentIndex].image} alt={`${testimonials[currentIndex].name} before and after transformation`} className="w-full h-auto object-contain" draggable="false" data-id="element-238" />
                    <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4 pb-3" data-id="element-239">
                      <span className="text-xs font-bold tracking-widest text-white uppercase bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm" data-id="element-240">
                        Before
                      </span>
                      <span className="text-xs font-bold tracking-widest text-white uppercase bg-gold/80 px-3 py-1 rounded-full backdrop-blur-sm" data-id="element-241">
                        After
                      </span>
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="p-8 flex flex-col flex-grow justify-center" data-id="element-242">
                    <div className="flex mb-6 justify-center" data-id="element-243">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-6 h-6 text-gold fill-current mx-0.5" data-id="element-244" />)}
                    </div>
                    <p className="text-gray-700 text-lg sm:text-xl leading-relaxed mb-8 text-center italic" data-id="element-245">
                      {testimonials[currentIndex].quote}
                    </p>
                    <div className="mt-auto text-center" data-id="element-246">
                      <p className="font-bold text-deep-teal tracking-wide text-lg" data-id="element-247">
                        {testimonials[currentIndex].name},{' '}
                        {testimonials[currentIndex].age}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 sm:-ml-6 z-10 bg-white p-3 rounded-full shadow-lg text-deep-teal hover:bg-cream hover:text-gold transition-colors focus:outline-none" onClick={() => paginate(-1)} aria-label="Previous testimonial" data-id="element-248">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-249" />
          </button>
          <button className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 sm:-mr-6 z-10 bg-white p-3 rounded-full shadow-lg text-deep-teal hover:bg-cream hover:text-gold transition-colors focus:outline-none" onClick={() => paginate(1)} aria-label="Next testimonial" data-id="element-250">
            <ChevronRightIcon className="w-6 h-6" data-id="element-251" />
          </button>

          {/* Dots */}
          <div className="flex justify-center space-x-3 mt-8" data-id="element-252">
            {testimonials.map((_, index) => <button key={index} onClick={() => {
            setDirection(index > currentIndex ? 1 : -1);
            setCurrentIndex(index);
          }} className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-gold scale-125' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-253" />)}
          </div>
        </div>
      </div>
    </section>;
}